import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@welldev-group/rcl-button';
import { Textarea } from '@welldev-group/react-comp-lib';
import SelectBox from '@welldev-group/rcl-select-box';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_BANNER } from '../../graphql/queries/banner';
import { CREATE_BANNER, EDIT_BANNER } from '../../graphql/mutations/banner';
import { useTranslation } from 'react-i18next';
import imageUrl from '../../helpers/imageUrl';
import clearCache from '../../helpers/clearCache';
import FileUpload from '../common/FileUpload';
import ErrorComponent from '../common/ErrorComponent';
import WithErrorHandler from '../common/WithErrorHandler';
import { getBanner } from '../../graphql/queries/banner';
import { debounce } from 'lodash';

const BannerForm = props => {
  const { t } = useTranslation();
  let { id } = useParams();
  const isNewRecord = !id;

  const [createBanner, callbackDataOnCreate] = useMutation(CREATE_BANNER, {
    update: cache => clearCache(cache, /Banner/),
  });
  const [editBanner, callbackDataOnEdit] = useMutation(EDIT_BANNER);
  const bannerFromId = useQuery(GET_BANNER, {
    variables: { id },
    skip: isNewRecord,
  });

  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [previousImage, setPreviousImage] = useState(null);
  const [publishDescription, setPublishDescription] = useState(false);

  const onDescriptionChange = event => {
    setDescription(event.target.value);
  };

  const onImageFileUpload = files => {
    setImage(files[0]);
  };

  const onPublishDescriptionChange = selectedOption => {
    setPublishDescription(selectedOption.value);
  };

  useEffect(() => {
    if (bannerFromId && bannerFromId.data && !bannerFromId.loading) {
      setDescription(bannerFromId.data.banner.description);
      setPreviousImage(bannerFromId.data.banner.imageUrls.large);
      setPublishDescription(bannerFromId.data.banner.publishDescription);
    }
  }, [bannerFromId]);

  const submitToBackEnd = debounce(() => {
    props.resetError();

    if (isNewRecord) {
      createBanner({
        variables: {
          description,
          publishDescription,
          image,
        },
      })
        .then(({ data }) => {
          props.history.push(`/admin/banners/${data.createBanner.id}`);
        })
        .catch(() => props.onError());
    } else {
      editBanner({
        variables: {
          id,
          description,
          publishDescription,
          image,
        },
        refetchQueries: [getBanner(id)],
      })
        .then(({ data }) => {
          props.history.push(`/admin/banners/${data.editBanner.id}`);
        })
        .catch(() => props.onError());
    }
  }, 500);

  const publishDescriptionOptions = [
    { value: true, label: t('shared.true') },
    { value: false, label: t('shared.false') },
  ];

  return (
    <div className="common-form">
      <div className="common-form__title">
        {isNewRecord ?
          t('shared.new', { entityName: t('banners.show.title') })
          : t('shared.editTitle', { entityName: t('banners.show.title') })}
      </div>
      <div className="common-form__error-message">
        {props.hasError && (
          <div className="col-6">
            <ErrorComponent error={{ callbackDataOnEdit, callbackDataOnCreate }} />
          </div>
        )}
      </div>
      <form className="common-form__container">
        <div className="grid">
          <div className="col-lg-6 col-md-10 col-sm-10 col-xs-10">
            <div className="col-lg-12 col-md-6 col-sm-6 col-xs-12 col-bleed-y">
              <div className="common-form__field banner-form__description">
                <Textarea
                  label={t('banners.attributes.description')}
                  value={description}
                  onChange={onDescriptionChange}
                  placeholder={''}
                />
              </div>
              <div className="common-form__field banner-form__publish-description">
                <SelectBox
                  label={t('banners.attributes.publishDescription')}
                  value={{
                    value: publishDescription,
                    label: t(`shared.boolean`, {
                      context: publishDescription,
                      defaultValue: publishDescription ? 'True' : 'False'
                    })
                  }}
                  options={publishDescriptionOptions}
                  onChange={onPublishDescriptionChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__submit-button">
                <Button
                  label={isNewRecord ? t('shared.create') : t('shared.update')}
                  onClick={submitToBackEnd}
                />
              </div>
            </div>
          </div>
          <div className="col-6 col-bleed-y">
            <h2>{t('banners.attributes.image')}</h2>
            {previousImage && (
              <div className="common-form__previous-image">
                <img src={imageUrl(previousImage)} alt="preview" />
              </div>
            )}
            <FileUpload onFileUpload={onImageFileUpload} multiple={false} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default WithErrorHandler(BannerForm);
